<template>
  <div>
    <v-card :disabled="isProcessing" :loading="isProcessing">
      <template v-slot:progress>
        <v-progress-linear absolute color="success" height="4" indeterminate></v-progress-linear>
      </template>
      <v-tabs v-model="tab" class="stock-color" left dark icons-and-text>
        <v-tabs-slider></v-tabs-slider>
        <v-tab href="#tab-1">
          <v-toolbar-title>{{ $t("STOCK.PURCHASE_ORDERS.TITLE") }}</v-toolbar-title>
        </v-tab>
        <v-spacer></v-spacer>
        <div class="buttons-tab">
          <v-btn
            v-if="hasPermissions('stock-purchase-orders-create')"
            class="mr-4 success"
            :disabled="isProcessing"
            @click="$router.push({ name: 'stock-purchase-orders-create' })"
          >
            {{ $t("GENERAL.LABELS.CREATE_BUTTON") }}
          </v-btn>
        </div>
      </v-tabs>
      <v-tabs-items v-model="tab">
        <v-tab-item value="tab-1">
          <v-card flat>
            <v-card-text class="pa-0">
              <Table
                v-if="userStatePrepared"
                ref="stock-purchase-orders-table"
                height="500"
                name="stock-purchase-orders-table"
                :headers="headers"
                :blink-row-item-key="blinkRowItemKey"
                :server-data-options="{ method: tableItemsMethod }"
                :items-per-page="25"
                :loading="isProcessing"
                :filters="tableFilters"
                dense
                fixed-header
                filterable
              >
                <template
                  v-for="(header, index) in headers"
                  v-slot:[`item.${header.value}`]="{ item }"
                >
                  <template v-if="header.value === 'code'">
                    <router-link
                      :key="index"
                      :to="{ name: 'stock-purchase-orders-view', params: { id: item.item.id } }"
                    >
                      {{ item.item.code }}
                    </router-link>
                  </template>
                  <template v-else-if="header.value === 'status'">
                    <v-chip :key="index" small :color="statusColor(item.item.status)" dark>
                      {{ statusLabel(item.item.status) }}
                    </v-chip>
                  </template>
                  <template v-else-if="header.value === 'supplier_company.label'">
                    {{ supplierLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'organization.name'">
                    {{ organizationLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'destination_warehouse.name'">
                    {{ warehouseLabel(item.item) }}
                  </template>
                  <template v-else-if="header.value === 'deal.label'">
                    <router-link
                      v-if="item.item.deal && item.item.deal.id"
                      :key="index"
                      :to="{ name: 'deals-view', params: { id: item.item.deal.id } }"
                    >
                      {{ dealLabel(item.item) }}
                    </router-link>
                    <span v-else :key="index">-</span>
                  </template>
                  <template v-else-if="header.value === 'total_gross'">
                    {{ formatMoney(item.item.total_gross, item.item.currency_code) }}
                  </template>
                  <template v-else>
                    {{ item.value || "-" }}
                  </template>
                </template>
              </Table>
            </v-card-text>
          </v-card>
        </v-tab-item>
      </v-tabs-items>
    </v-card>
  </div>
</template>

<script>
import Table from "@/views/partials/v-table/Table.vue";
import userStateService from "@/common/userState.service";
import { GET_STOCK_PURCHASE_ORDERS } from "@/store/stock/stockPurchaseOrders.module";
import { GET_STOCK_DEAL_OPTIONS } from "@/store/stock/stockReceipts.module";

export default {
  name: "StockListPurchaseOrders",
  components: { Table },
  data() {
    return {
      tableName: "stock-purchase-orders-table",
      blinkRowItemKey: null,
      userStatePrepared: false,
      tab: "tab-1",
      isProcessing: false,
      tableItemsMethod: GET_STOCK_PURCHASE_ORDERS,
      tableFilters: [],
      headers: [
        { text: this.$t("STOCK.PURCHASE_ORDERS.DOCUMENT_CODE"), value: "code", sortable: true, align: "center", width: "10%" },
        { text: this.$t("GENERAL.LABELS.STATUS"), value: "status", sortable: true, align: "center", width: "9%" },
        { text: this.$t("STOCK.PURCHASE_ORDERS.SUPPLIER"), value: "supplier_company.label", sortable: false, align: "center", width: "14%" },
        { text: this.$t("STOCK.WAREHOUSES.ORGANIZATION"), value: "organization.name", sortable: false, align: "center", width: "10%" },
        { text: this.$t("STOCK.PURCHASE_ORDERS.DESTINATION_WAREHOUSE"), value: "destination_warehouse.name", sortable: false, align: "center", width: "12%" },
        { text: this.$t("STOCK.PURCHASE_ORDERS.DEAL"), value: "deal.label", sortable: false, align: "center", width: "14%" },
        { text: this.$t("STOCK.PURCHASE_ORDERS.ORDER_DATE"), value: "order_date", sortable: true, align: "center", width: "9%" },
        { text: this.$t("STOCK.PURCHASE_ORDERS.TOTAL_GROSS"), value: "total_gross", sortable: false, align: "center", width: "9%" },
      ],
    };
  },
  computed: {
    stockContextVersion() {
      return this.$store.getters.stockContextVersion;
    },
  },
  watch: {
    stockContextVersion() {
      this.loadTableFilters().finally(() => this.reloadTable());
    },
  },
  mounted() {
    this.blinkRowItemKey = userStateService.getState(
      `table-state.${this.tableName}.blinkRowItemKey`,
      null
    );
    this.loadTableFilters().finally(() => {
      this.userStatePrepared = true;
    });
  },
  methods: {
    supplierLabel(row) {
      const company = row.supplier_company;
      if (!company) {
        return "-";
      }
      return company.label || company.name || "-";
    },
    organizationLabel(row) {
      const org = row.organization;
      if (!org) {
        return "-";
      }
      return org.name || org.code || "-";
    },
    warehouseLabel(row) {
      const warehouse = row.destination_warehouse;
      if (!warehouse) {
        return "-";
      }
      if (warehouse.code && warehouse.name) {
        return `${warehouse.code} — ${warehouse.name}`;
      }
      return warehouse.name || warehouse.code || "-";
    },
    dealLabel(row) {
      const deal = row.deal;
      if (!deal) {
        return "-";
      }
      if (deal.code && deal.name) {
        return `${deal.code} — ${deal.name}`;
      }
      return deal.name || deal.code || "-";
    },
    loadTableFilters() {
      const statusItems = [
        "draft",
        "sent",
        "confirmed",
        "partially_received",
        "received",
        "cancelled",
      ].map((status) => ({
        id: status,
        label: this.$te(`STOCK.STATUSES.PO.${status.toUpperCase()}`)
          ? this.$t(`STOCK.STATUSES.PO.${status.toUpperCase()}`)
          : status,
      }));

      const organizationIds = this.$store.getters.stockContextOrganizationIds;

      const dealParams = { limit: 100 };
      if (organizationIds.length === 1) {
        dealParams.organization_id = organizationIds[0];
      } else if (organizationIds.length > 1) {
        dealParams.organization_ids = organizationIds;
      }

      return this.$store.dispatch(GET_STOCK_DEAL_OPTIONS, dealParams)
        .then((deals) => {
          const dealItems = (deals || []).map((deal) => ({
            id: deal.id,
            label: deal.code ? `${deal.code} — ${deal.name}` : deal.name,
          }));

          this.tableFilters = [
            {
              key: "status",
              label: this.$t("GENERAL.LABELS.STATUS"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: statusItems,
            },
            {
              key: "deal_id",
              label: this.$t("STOCK.PURCHASE_ORDERS.DEAL"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: dealItems,
            },
          ];
        })
        .catch(() => {
          this.tableFilters = [
            {
              key: "status",
              label: this.$t("GENERAL.LABELS.STATUS"),
              type: "select",
              "item-value": "id",
              "item-text": "label",
              items: statusItems,
            },
          ];
        });
    },
    reloadTable() {
      this.$nextTick(() => {
        this.$refs["stock-purchase-orders-table"]?.fetchServerData();
      });
    },
    statusColor(status) {
      const map = {
        draft: "grey",
        sent: "grey",
        confirmed: "primary",
        partially_received: "orange",
        received: "success",
        cancelled: "error",
      };
      return map[status] || "grey";
    },
    statusLabel(status) {
      const key = `STOCK.STATUSES.PO.${(status || "").toUpperCase()}`;
      return this.$te(key) ? this.$t(key) : status;
    },
    formatMoney(value, currency) {
      return `${Number(value || 0).toFixed(2)} ${currency || ""}`.trim();
    },
  },
};
</script>
